
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import HeroBackground from './hero/HeroBackground';
import HeroContent from './hero/HeroContent';

interface HeroSectionProps {
  title?: string;
  subtitle?: string;
  description?: string;
  industry?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ 
  title, 
  subtitle, 
  description, 
  industry 
}) => {
  const { currentLanguage } = useLanguage();
  const isMobile = useIsMobile();
  
  // Get the correct heading based on language with SEO-optimized keywords
  const getHeadingText = () => {
    if (title) return title;
    
    switch(currentLanguage) {
      case 'de':
        return 'KI-Video Marketing für Social Media Ads';
      case 'en':
        return 'AI Video Marketing for Social Media Ads';
      case 'fr':
        return 'Marketing vidéo IA pour les publicités sur médias sociaux';
      case 'it':
        return 'Video marketing con IA per pubblicità sui social media';
      default:
        return 'KI-Video Marketing für Social Media Ads';
    }
  };
  
  const getSubtitleText = () => {
    if (subtitle) return subtitle;
    
    return currentLanguage === 'de' ? "KI-Video Marketing Expertise für Ihren Erfolg auf Facebook, Instagram & TikTok" :
           currentLanguage === 'en' ? "AI Video Marketing Expertise for Your Success on Facebook, Instagram & TikTok" :
           currentLanguage === 'fr' ? "Expertise en marketing vidéo IA pour votre succès sur Facebook, Instagram et TikTok" :
           "Competenza di video marketing con IA per il tuo successo su Facebook, Instagram e TikTok";
  };
  
  // Get SEO-optimized subheading text
  const getSubheadingText = () => {
    if (description) return description;
    
    switch(currentLanguage) {
      case 'de':
        return 'Professionelle KI-optimierte Videos für mehr Engagement auf Ihren Social Media Kanälen';
      case 'en':
        return 'Professional AI-optimized videos for more engagement on your social media channels';
      case 'fr':
        return 'Vidéos professionnelles optimisées par l\'IA pour plus d\'engagement sur vos canaux de médias sociaux';
      case 'it':
        return 'Video professionali ottimizzati con IA per più coinvolgimento sui tuoi canali social media';
      default:
        return 'Professionelle KI-optimierte Videos für mehr Engagement auf Ihren Social Media Kanälen';
    }
  };

  // Determine background video based on industry (if provided)
  const getBackgroundVideo = () => {
    if (!industry) return "https://streamable.com/e/b02fqo?autoplay=1&nocontrols=1&muted=1&loop=1";
    
    // You could add industry-specific videos here
    const industryVideos: Record<string, string> = {
      'finance': "https://streamable.com/e/b02fqo?autoplay=1&nocontrols=1&muted=1&loop=1",
      'luxury': "https://streamable.com/e/b02fqo?autoplay=1&nocontrols=1&muted=1&loop=1", 
      'tourism': "https://streamable.com/e/b02fqo?autoplay=1&nocontrols=1&muted=1&loop=1",
      'healthcare': "https://streamable.com/e/b02fqo?autoplay=1&nocontrols=1&muted=1&loop=1"
    };
    
    return industryVideos[industry] || "https://streamable.com/e/b02fqo?autoplay=1&nocontrols=1&muted=1&loop=1";
  };

  return (
    <section 
      className={`relative ${isMobile ? 'min-h-[700px]' : 'h-[85vh]'} min-h-[600px] flex items-center justify-center overflow-hidden pt-16 md:pt-20`}
      aria-labelledby="hero-heading"
    >
      {/* Video Background */}
      <HeroBackground videoUrl={getBackgroundVideo()} />
      
      {/* Content */}
      <div className="container mx-auto px-4 z-20 relative text-center mt-8 md:mt-16">
        <HeroContent 
          title={getHeadingText()}
          subtitle={getSubtitleText()}
          subheading={getSubheadingText()}
        />
      </div>
    </section>
  );
};

export default HeroSection;
